import React from "react";
import { SectionCard, inputCls } from "./ui";
import { useProject } from "@/context/ProjectContext";
import { uid } from "@/lib/uid";
import SupportingMaterials from "./SupportingMaterials";
import { Plus, Trash2, MessageSquare, ShieldAlert } from "lucide-react";

const LIKELIHOOD = ["Low", "Medium", "High"];
const LIK_COLOR = { Low: "bg-emerald-100 text-emerald-700", Medium: "bg-amber-100 text-amber-700", High: "bg-red-100 text-red-700" };

export default function Phase5() {
  const { phaseData: pd, setPhase } = useProject();
  const p = pd.phase5 || { communication_plan: [], risks: [] };
  const comms = p.communication_plan || [];
  const risks = p.risks || [];

  const setComms = (c) => setPhase("phase5", { ...p, communication_plan: c });
  const setRisks = (r) => setPhase("phase5", { ...p, risks: r });

  const addComm = () => setComms([...comms, { id: uid(), stakeholder: "", message: "", channel: "", frequency: "" }]);
  const updComm = (i, k, v) => { const c = [...comms]; c[i] = { ...c[i], [k]: v }; setComms(c); };
  const delComm = (i) => setComms(comms.filter((_, idx) => idx !== i));

  const addRisk = () => setRisks([...risks, { id: uid(), risk: "", likelihood: "Medium", mitigation: "" }]);
  const updRisk = (i, k, v) => { const r = [...risks]; r[i] = { ...r[i], [k]: v }; setRisks(r); };
  const delRisk = (i) => setRisks(risks.filter((_, idx) => idx !== i));

  const transfer = p.transfer_plan || {};
  const setTransfer = (k, v) => setPhase("phase5", { ...p, transfer_plan: { ...transfer, [k]: v } });

  return (
    <div className="space-y-6 max-w-5xl">
      <SectionCard
        title="Stakeholder Communication Plan"
        desc="Who needs to hear what, how, and how often."
        right={<button data-testid="add-comm-btn" onClick={addComm} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
        testid="phase5-comms"
      >
        <div className="space-y-3">
          {comms.length === 0 && <p className="text-sm text-slate-400 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> No communications planned yet.</p>}
          {comms.map((c, i) => (
            <div key={c.id || i} className="grid sm:grid-cols-[1fr_1.4fr_1fr_1fr_auto] gap-2 items-center" data-testid={`comm-row-${i}`}>
              <input value={c.stakeholder} onChange={(e) => updComm(i, "stakeholder", e.target.value)} placeholder="Stakeholder" className={inputCls} data-testid={`comm-stakeholder-${i}`} />
              <input value={c.message} onChange={(e) => updComm(i, "message", e.target.value)} placeholder="Key message" className={inputCls} />
              <input value={c.channel} onChange={(e) => updComm(i, "channel", e.target.value)} placeholder="Channel" className={inputCls} />
              <input value={c.frequency} onChange={(e) => updComm(i, "frequency", e.target.value)} placeholder="Frequency" className={inputCls} />
              <button onClick={() => delComm(i)} className="text-slate-300 hover:text-red-500 justify-self-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Risk & Resistance Mitigation Log"
        desc="Anticipate resistance and document mitigation."
        right={<button data-testid="add-risk-btn" onClick={addRisk} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
        testid="phase5-risks"
      >
        <div className="space-y-3">
          {risks.length === 0 && <p className="text-sm text-slate-400 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> No risks logged yet.</p>}
          {risks.map((r, i) => (
            <div key={r.id || i} className="grid sm:grid-cols-[1.4fr_auto_1.4fr_auto] gap-2 items-center" data-testid={`risk-row-${i}`}>
              <input value={r.risk} onChange={(e) => updRisk(i, "risk", e.target.value)} placeholder="Risk / source of resistance" className={inputCls} data-testid={`risk-desc-${i}`} />
              <select value={r.likelihood} onChange={(e) => updRisk(i, "likelihood", e.target.value)} className={`${inputCls} w-32 ${LIK_COLOR[r.likelihood]}`} data-testid={`risk-likelihood-${i}`}>
                {LIKELIHOOD.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <input value={r.mitigation} onChange={(e) => updRisk(i, "mitigation", e.target.value)} placeholder="Mitigation action" className={inputCls} />
              <button onClick={() => delRisk(i)} className="text-slate-300 hover:text-red-500 justify-self-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Learning Transfer Plan"
        desc="Ensure learning transfers to on-the-job performance — supports, reinforcement, follow-up and self-monitoring (Design Document)."
        testid="phase5-transfer"
      >
        <div className="grid sm:grid-cols-2 gap-3">
          {[["environmental_supports", "Environmental Supports"], ["manager_peer", "Manager & Peer Reinforcement"], ["follow_up", "Follow-up & Practice Opportunities"], ["self_monitoring", "Self-Monitoring Opportunities"]].map(([k, label]) => (
            <div key={k}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
              <textarea data-testid={`transfer-${k}`} value={transfer[k] || ""} onChange={(e) => setTransfer(k, e.target.value)} rows={2} className={`${inputCls} resize-y`} placeholder={`${label}…`} />
            </div>
          ))}
        </div>
      </SectionCard>

      <SupportingMaterials phaseKey="phase5" />
    </div>
  );
}
